import { all, call, getContext, put, takeLatest } from "redux-saga/effects";

import { CONTEXT } from "@const/redux";
import { normalizePost, normalizePostArray } from "@helpers/posts";
import { setAuthors } from "../authors/actions";
import { updateSubscriptions } from "../subscriptions/actions";
import {
  getAllPostsBySubscriptionFailure,
  getAllPostsBySubscriptionSuccess,
  getMorePostsBySubscriptionFailure,
  getMorePostsBySubscriptionSuccess,
  getPostsBySubscriptionFailure,
  getPostsBySubscriptionSuccess,
  getSibilingPostsFailure,
  getSibilingPostsSuccess,
  setPostsBySubscription,
} from "./actions";
import { setPosts } from "../posts/actions";
import {
  getAllPostsBySubscriptionActionTypes,
  getMorePostsBySubscriptionActionTypes,
  getPostsBySubscriptionActionTypes,
  getSibilingPostsInSubscroptionActionTypes,
} from "./actionTypes";
import {
  setSubscriptionDetailsPosts,
  addSubscriptionDetailsPosts,
} from "../subscriptionDetails/actions";
import { setSibilings } from "../postDetails/actions";

const getPostsBySubscriptionAsync = ({ SubscriptionsService, id, params }) => {
  if (!SubscriptionsService) {
    return;
  }

  return SubscriptionsService.getSubscriptionPosts(id, params);
};

function* getAllPostsBySubscription({ payload }) {
  try {
    const limit = 100;
    const { id } = payload;
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);

    let fetchedPosts = [];
    let isDone = false;

    while (!isDone) {
      const { data, pagination } = yield call(getPostsBySubscriptionAsync, {
        id,
        SubscriptionsService,
        params: {
          offset: fetchedPosts?.length,
          limit,
        },
      });

      const { totalCount } = pagination;

      fetchedPosts = fetchedPosts.concat(data);

      isDone = totalCount === fetchedPosts.length;
    }

    const { entities, result: allPosts } = normalizePostArray(fetchedPosts);
    const { authors, subscriptions, posts: postById } = entities;

    yield all([
      put(setAuthors(authors)),
      put(updateSubscriptions({ subscriptions })),
      put(setPostsBySubscription({ id, allPosts })),
      put(setPosts({ postById })),
      put(getAllPostsBySubscriptionSuccess({ postById })),
    ]);
  } catch (error) {
    yield put(getAllPostsBySubscriptionFailure());
  }
}

function* getPostsBySubscription({ payload }) {
  try {
    const { id, offset, limit } = payload;
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getPostsBySubscriptionAsync, {
      id,
      SubscriptionsService,
      params: { offset, limit },
    });

    const { totalCount } = pagination;

    const { entities, result: allPosts } = normalizePostArray(data);
    const { authors, subscriptions, posts: postById } = entities;

    yield all([
      put(setAuthors(authors)),
      put(updateSubscriptions({ subscriptions })),
      put(getPostsBySubscriptionSuccess({ id, allPosts, postById })),
      put(setSubscriptionDetailsPosts({ id, totalCount, allPosts })),
    ]);
  } catch (e) {
    console.error(e);
    yield put(getPostsBySubscriptionFailure());
  }
}

function* getMorePostsBySubscription({ payload }) {
  try {
    const { id, offset, limit, direction } = payload;
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);

    const { data, pagination } = yield call(getPostsBySubscriptionAsync, {
      id,
      SubscriptionsService,
      params: { offset, limit, direction },
    });

    const { totalCount } = pagination;

    const { entities, result: allPosts } = normalizePostArray(data);
    const { authors, subscriptions, posts: postById } = entities;

    yield all([
      put(setAuthors(authors)),
      put(updateSubscriptions({ subscriptions })),
      put(getMorePostsBySubscriptionSuccess({ id, allPosts, postById })),
      put(addSubscriptionDetailsPosts({ id, totalCount, allPosts })),
    ]);
  } catch (e) {
    console.error(e);
    yield put(getMorePostsBySubscriptionFailure());
  }
}

function* getSibilingPostsInSubscroption({ payload }) {
  try {
    const { subscription: id, offset } = payload;
    const { SubscriptionsService } = yield getContext(CONTEXT.SERVICES);

    const prevOffset = offset - 1;
    const nextOffset = parseInt(offset) + 1;

    let prevPostData;

    if (!(prevOffset < 0)) {
      const { data } = yield call(getPostsBySubscriptionAsync, {
        id,
        SubscriptionsService,
        params: { offset: prevOffset, limit: 1 },
      });

      prevPostData = data;
    }

    const { data: nextPostData } = yield call(getPostsBySubscriptionAsync, {
      id,
      SubscriptionsService,
      params: { offset: nextOffset, limit: 1 },
    });

    const {
      entities: { posts: prevPostById },
      result: prevPostId,
    } = normalizePost(prevPostData?.[0] || {});
    const {
      entities: { posts: nextPostById },
      result: nextPostId,
    } = normalizePost(nextPostData?.[0] || {});

    const prevPost = prevPostById[prevPostId];
    const nextPost = nextPostById[nextPostId];

    yield all([
      put(
        getSibilingPostsSuccess({
          prevPost,
          prevPostId,
          nextPost,
          nextPostId,
        }),
      ),
      put(
        setSibilings({
          prevPostId,
          prevPostKey: prevPost?.uniqueKey,
          nextPostId,
          nextPostKey: nextPost?.uniqueKey,
        }),
      ),
    ]);
  } catch (error) {
    yield put(getSibilingPostsFailure());
    console.log(error);
  }
}

export function* watchGetAllPostsBySubscription() {
  yield takeLatest(
    getAllPostsBySubscriptionActionTypes.request,
    getAllPostsBySubscription,
  );
}

export function* watchGetPostsBySubscription() {
  yield takeLatest(
    getPostsBySubscriptionActionTypes.request,
    getPostsBySubscription,
  );
}

export function* watchGetMorePostsBySubscription() {
  yield takeLatest(
    getMorePostsBySubscriptionActionTypes.request,
    getMorePostsBySubscription,
  );
}

export function* watchGetSibilingPostsInSubscroption() {
  yield takeLatest(
    getSibilingPostsInSubscroptionActionTypes.request,
    getSibilingPostsInSubscroption,
  );
}
